import { Global, css } from '@emotion/react';
import { useLocation } from 'react-router-dom';
import { SITRA_TOKENS } from './sitraTokens';

export const GlobalStyles = () => {
  const location = useLocation();

  // Pages that should NOT scroll (full-screen displays, immersive views)
  // Scrolling is ON by default - only disable for specific pages
  const noScrollPages = ['/table', '/tablet', '/weather', '/statmap', '/postit', '/impact-machine', '/impactpath', '/impactmap', '/pivotpoints', '/tunnelma', '/swot', '/kuutio', '/slides'];
  const noScroll = noScrollPages.includes(location.pathname) || location.pathname.startsWith('/screen/');

  return (
    <Global
      styles={css`
      ${SITRA_TOKENS}

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      :root {
        /* Visible viewport — updated by useViewportSync(); dvh/svh fallbacks until JS runs */
        --kuutio-vh: 100vh;
        --kuutio-vw: 100vw;

        /* KUUTIO Brand Colors */
        --kuutio-blue: #0099ff;
        --kuutio-yellow: #ffcc00;
        --kuutio-pink: #ff0066;
        --kuutio-green: #00ff88;
        --kuutio-purple: #9933ff;
        
        /* Neutral Colors */
        --black: #000000;
        --dark-gray: #1a1a1a;
        --gray: #666666;
        --light-gray: #cccccc;
        --white: #ffffff;
        
        /* Gradients */
        --gradient-aurora: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        --gradient-sunset: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        --gradient-ocean: linear-gradient(135deg, #0099ff 0%, #00ff88 100%);
        --gradient-neon: linear-gradient(135deg, #ffcc00 0%, #ff0066 100%);
        
        /* Typography */
        --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        --font-mono: 'JetBrains Mono', 'Courier New', monospace;
      }

      @supports (height: 100dvh) {
        :root {
          --kuutio-vh: 100dvh;
          --kuutio-vw: 100dvw;
        }
      }

      html, body {
        font-family: var(--font-sans);
        color: var(--white);
        background: var(--black);
        overflow-y: ${noScroll ? 'hidden' : 'auto'};
        /* Lock horizontal scrolling at the root. A wide child (an absent
         * min-width:0 on a flex item, a long inline string, etc) would
         * otherwise let the body extend past 100vw on mobile — the user
         * sees content sliding off-screen. We want that fixed at the root
         * regardless of which descendant misbehaved. */
        overflow-x: hidden;
        max-width: 100vw;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      @supports (height: 100dvh) {
        html, body {
          min-height: 100dvh;
        }
      }

      h1, h2, h3, h4, h5, h6 {
        font-weight: 600;
        line-height: 1.2;
      }

      h1 { font-size: 3rem; }
      h2 { font-size: 2.25rem; }
      h3 { font-size: 1.875rem; }
      h4 { font-size: 1.5rem; }
      h5 { font-size: 1.25rem; }
      h6 { font-size: 1.125rem; }

      @keyframes kuutio-spin { to { transform: rotate(360deg); } }
      .kuutio-spin { animation: kuutio-spin 0.9s linear infinite; transform-box: fill-box; transform-origin: center; }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }

      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-20px); }
      }

      /* Scrollbar Styling */
      ::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }

      ::-webkit-scrollbar-track {
        background: var(--dark-gray);
      }

      ::-webkit-scrollbar-thumb {
        background: var(--gray);
        border-radius: 4px;
      }

      ::-webkit-scrollbar-thumb:hover {
        background: var(--light-gray);
      }

      /*
       * Samsung Tizen Smart Signage / TV browser overrides.
       * Toggled via data-platform="tizen" on <html> in main.jsx based on UA.
       *
       * Issues observed on 90" Samsung touchscreens running Tizen browser:
       *   - backdrop-filter: blur(N) on full-screen overlays causes flicker,
       *     compositing layer corruption ("HW error"-looking artifacts), and
       *     mid-frame redraws when content underneath updates.
       *   - -webkit-background-clip: text + transparent fill leaves garbage
       *     pixels — handled per-component via styles/mixins.js gradientText().
       *   - Infinite shimmer animations keep repainting layers the GPU
       *     cannot accommodate (same uninitialised-memory spillover).
       *   - Two-tone linear-gradient buttons band into a hard seam line
       *     (handled per-component with solid mid-tone fills).
       *
       * Trade-off: backdrops lose blur; shimmer bars become static. Barely
       * noticeable at workshop viewing distance on 90" screens.
       */
      html[data-platform='tizen'] *,
      html[data-platform='tizen'] *::before,
      html[data-platform='tizen'] *::after {
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
      }

      /* Infinite shimmer / loading bars — stop the GPU-repaint loop. */
      html[data-platform='tizen'] .shimmer {
        animation: none !important;
        background: rgba(168, 85, 247, 0.25) !important;
        background-size: auto !important;
      }
    `}
    />
  );
};